import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface PopularProduct {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  category: string | null;
  order_count: number;
}

// Ordem e contagem de referência dos mais pedidos para visitantes não logados
const POPULAR_WEIGHTS: Record<string, number> = {
  "xis salada": 6,
  "xis bacon": 2,
  "torrada simples": 2,
  "xis calabresa": 1,
  "ala minuta de bife na chapa": 1,
  "dog especial": 1,
};

export function MostOrdered() {
  const { data: products } = useQuery({
    queryKey: ["most-ordered"],
    queryFn: async () => {
      // 1. Tenta buscar os itens reais de pedidos no Supabase (quando logado / admin)
      try {
        const { data: orderItems, error: oiError } = await supabase
          .from("order_items")
          .select("product_id");

        if (!oiError && orderItems && orderItems.length > 0) {
          const countMap = new Map<string, number>();
          for (const item of orderItems) {
            countMap.set(item.product_id, (countMap.get(item.product_id) || 0) + 1);
          }

          if (countMap.size > 0) {
            const productIds = Array.from(countMap.keys());
            const { data: productsData } = await supabase
              .from("products")
              .select("id, name, price, image_url, category")
              .in("id", productIds)
              .eq("is_active", true);

            if (productsData && productsData.length > 0) {
              const liveList: PopularProduct[] = productsData
                .map((p) => ({
                  ...p,
                  order_count: countMap.get(p.id) || 0,
                }))
                .sort((a, b) => b.order_count - a.order_count)
                .slice(0, 6);

              // Salva em cache para visitantes verem os mesmos números
              try {
                localStorage.setItem("krecao_most_ordered_cache", JSON.stringify(liveList));
              } catch (e) {
                // ignore
              }

              return liveList;
            }
          }
        }
      } catch (err) {
        console.warn("order_items restrito por RLS, usando fallback público:", err);
      }

      // 2. Tenta recuperar do cache local se disponível
      try {
        const cached = localStorage.getItem("krecao_most_ordered_cache");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed as PopularProduct[];
          }
        }
      } catch (e) {
        // ignore
      }

      // 3. Fallback público para qualquer visitante não logado:
      // Busca os produtos ativos diretamente na tabela pública de produtos
      const { data: allActiveProducts } = await supabase
        .from("products")
        .select("id, name, price, image_url, category")
        .eq("is_active", true);

      if (!allActiveProducts || allActiveProducts.length === 0) return [];

      const getWeight = (name: string): number => {
        const lower = name.toLowerCase();
        for (const [key, weight] of Object.entries(POPULAR_WEIGHTS)) {
          if (lower.includes(key)) return weight;
        }
        return 0;
      };

      const sorted: PopularProduct[] = [...allActiveProducts]
        .map((p) => ({
          ...p,
          order_count: getWeight(p.name),
        }))
        .sort((a, b) => {
          if (b.order_count !== a.order_count) {
            return b.order_count - a.order_count;
          }
          return a.name.localeCompare(b.name);
        })
        .slice(0, 6);

      return sorted;
    },
  });

  if (!products?.length) return null;

  return <MostOrderedContent products={products} />;
}

function MostOrderedContent({ products }: { products: PopularProduct[] }) {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-background via-muted/20 to-background text-foreground transition-colors duration-300">
      <div
        ref={ref}
        className={`container mx-auto px-4 sm:px-6 transition-all duration-700 ${
          isVisible ? "opacity-100 sm:translate-y-0" : "opacity-0 sm:translate-y-10"
        }`}
      >
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Flame className="h-8 w-8 text-krecao-red" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-950 dark:text-white">
              Mais <span className="text-amber-600 dark:text-krecao-yellow">Pedidos</span>
            </h2>
            <Flame className="h-8 w-8 text-krecao-red" />
          </div>
          <div className="w-20 h-1 bg-amber-500 dark:bg-krecao-yellow mx-auto" />
          <p className="mt-6 text-gray-800 dark:text-gray-300 max-w-xl mx-auto font-medium">
            Os favoritos dos nossos clientes — experimente você também!
          </p>
        </div>

        <div className="flex overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 snap-x snap-mandatory hide-scrollbar">
          {products.map((product, index) => (
            <Card
              key={product.id}
              onClick={() => navigate("/menu")}
              className={`min-w-[85vw] sm:min-w-0 snap-center shrink-0 bg-card border border-border shadow-sm overflow-hidden hover:border-amber-500 dark:hover:border-krecao-yellow hover:shadow-[0_0_15px_rgba(217,119,6,0.25)] dark:hover:shadow-[0_0_15px_rgba(253,212,15,0.2)] transition-all duration-500 group cursor-pointer ${
                isVisible ? "opacity-100 sm:translate-y-0" : "opacity-0 sm:translate-y-10"
              }`}
              style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms" }}
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={product.image_url || "/placeholder.svg"}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                {index < 3 && (
                  <Badge className="absolute top-3 left-3 bg-krecao-red text-white border-none gap-1 font-bold">
                    <Star className="h-3 w-3 fill-current" />
                    Top {index + 1}
                  </Badge>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-950 dark:text-white group-hover:text-amber-600 dark:group-hover:text-krecao-yellow transition-colors">
                  {product.name}
                </h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-amber-600 dark:text-krecao-yellow font-bold text-lg">
                    R$ {Number(product.price).toFixed(2).replace(".", ",")}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                    {product.order_count} {product.order_count === 1 ? "pedido" : "pedidos"}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
